import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  claimReferralReward,
  getReferralRewardDetails
} from '../../../api/referral.api';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import { useReferralCode } from '../../../hooks/useReferralCode';
import { IGetReferralRewardDetailsResponse } from '../../../types/referral.types';

const ReferralDialog: React.FC = () => {
  const [openReferralDialog, setOpenReferralDialog] = useState(false);
  const [isCollected, setIsCollected] = useState(false);
  const { refCode, clearReferralCode } = useReferralCode();
  const navigate = useNavigate();
  const [refDetails, setRefDetails] = useState<
    IGetReferralRewardDetailsResponse['data'] | null
  >(null);

  // Fetch referral reward data only if refCode exists
  const {
    data: refResponse,
    isLoading,
    isError,
    isSuccess
  } = useQuery({
    queryKey: ['referralReward', refCode],
    queryFn: () => getReferralRewardDetails(refCode!),
    enabled: !!refCode,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity
  });

  // Claim referral reward mutation
  const claimMutation = useMutation({
    mutationFn: (code: string) => claimReferralReward(code),
    onSuccess: () => {
      setIsCollected(true);
      clearReferralCode();
    },
    onError: (error) => {
      console.error('Failed to claim referral reward:', error);
    }
  });

  useEffect(() => {
    if (isSuccess && !refResponse?.data.exists) {
      setOpenReferralDialog(false);
      clearReferralCode();
      return;
    }
    if (isSuccess && refResponse?.data?.exists) {
      setOpenReferralDialog(true);
      setRefDetails(refResponse?.data);
    }
  }, [clearReferralCode, isSuccess, refResponse]);

  const handleCollect = () => {
    console.log('- claim reward clicked');
    if (refCode) {
      claimMutation.mutate(refCode);
    }
  };

  const handleViewRewards = () => {
    console.log('- handle view reward called');
    console.log('- isAyeReward:', refDetails?.isAyeReward);
    console.log('- closing dialog...');

    setOpenReferralDialog(false);
    setRefDetails(null);

    if (refDetails?.isAyeReward) {
      navigate('/my-rewards-hub/aye');
    } else {
      navigate(`/my-rewards-hub/${refDetails?.entity_id}/locations`);
    }
    // // Use setTimeout to ensure navigation happens after dialog closes
    // setTimeout(() => {
    //   console.log('- navigating...');
    // }, 100);
  };

  return (
    <Dialog
      open={openReferralDialog}
      onOpenChange={(open) => {
        if (!open) {
          setOpenReferralDialog(false);
          clearReferralCode();
          setRefDetails(null);
        }
      }}
    >
      <DialogContent className="max-w-[24rem] rounded-3xl">
        <DialogHeader>
          <DialogTitle className="mt-8 text-center font-normal">
            {isCollected ? 'Congratulations!' : 'Your Referee Reward is Here!'}
          </DialogTitle>
        </DialogHeader>

        <div className="m-2 flex flex-col items-center text-sm">
          {(isLoading || claimMutation.isPending) && <p>Loading reward...</p>}
          {(isError || claimMutation.isError) && <p>Failed to load reward.</p>}
          {refDetails && (
            <p className="mb-4 text-center">
              {isCollected
                ? `you've earned referral reward of ${refDetails?.refAmount || '[amount]'} ${refDetails?.refRewardType || '[reward type]'}!`
                : `Claim your ${refDetails?.refAmount || '[amount]'} ${refDetails?.refRewardType || '[reward type]'} reward now and start using AyeFace ${refDetails?.entityName ? ' at ' + refDetails?.entityName : 'at [brand]'} to unlock even more rewards!`}
            </p>
          )}
          {isCollected && !refDetails?.isAyeReward && (
            <p className="mb-4 text-center">
              {`Visit ${refDetails?.entityName || 'the brand'} outlets with AyeFace (Basic/Pro Plan) to redeem your rewards`}
            </p>
          )}

          {isCollected ? (
            <Button className="mb-2 mt-4 w-64" onClick={handleViewRewards}>
              {refDetails?.isAyeReward ? 'See My Rewards' : 'See Locations'}
            </Button>
          ) : (
            <Button
              className="mb-2 mt-4 w-64"
              onClick={handleCollect}
              disabled={isLoading || isError || claimMutation.isPending}
            >
              {claimMutation.isPending ? 'Claiming...' : 'Claim Reward'}
            </Button>
          )}

          {!isCollected && (
            <p className="text-xs italic text-black/70">
              You won't see this again if you cancel it
            </p>
          )}
        </div>

        {isCollected && (
          <div className="pointer-events-none absolute -z-10 px-4">
            <img src="/assets/congratz-bg.png" alt="Congratulations" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReferralDialog;
